/**
 * Short beep cues for recording lifecycle (start / pause / stop).
 * Uses Web Audio API — no external audio files required.
 * Call unlockRecordingSounds() from a user gesture before first playback (iPad Safari).
 */

type BeepKind = 'start' | 'pause' | 'stop';
const AUDIO_GAIN_MODERATE = 0.12;

let audioContext: AudioContext | null = null;
let unlocked = false;

type WebkitWindow = Window & {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
};

function getAudioContext(): AudioContext | null {
  const win = globalThis.window as WebkitWindow | undefined;
  const Ctor = win?.AudioContext ?? win?.webkitAudioContext;

  if (!Ctor) {
    return null;
  }
  if (!audioContext || audioContext.state === 'closed') {
    audioContext = new Ctor();
  }
  return audioContext;
}

/** Prime audio on first user tap (required on iOS Safari). */
export function unlockRecordingSounds(): void {
  const ctx = getAudioContext();
  if (!ctx) {
    return;
  }
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {
      // biome-ignore lint/suspicious/noConsole: reason here
      console.warn('Unable to unlock recording sounds');
    });
  }
  unlocked = true;
}

function playTone(opts: {
  frequency: number;
  durationMs: number;
  type?: OscillatorType;
  gain?: number;
  delayMs?: number;
}) {
  const ctx = getAudioContext();
  if (!ctx || !unlocked) {
    return;
  }

  try {
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }

    const startAt = ctx.currentTime + (opts.delayMs ?? 0) / 1000;
    const stopAt = startAt + opts.durationMs / 1000;

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.type = opts.type ?? 'sine';
    oscillator.frequency.setValueAtTime(opts.frequency, startAt);

    const peak = opts.gain ?? AUDIO_GAIN_MODERATE;
    gainNode.gain.setValueAtTime(0.0001, startAt);
    gainNode.gain.exponentialRampToValueAtTime(peak, startAt + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, stopAt);

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start(startAt);
    oscillator.stop(stopAt + 0.02);
  } catch {
    // Beep is optional UX; never block recording flow.
  }
}

function playBeep(kind: BeepKind) {
  switch (kind) {
    case 'start':
      // Ascending cue — recording began
      playTone({ frequency: 880, durationMs: 100, gain: 0.4 });
      playTone({ frequency: 1175, durationMs: 120, gain: 0.4, delayMs: 105 });
      break;
    case 'pause':
      // Mid tone — paused
      playTone({
        frequency: 520,
        durationMs: 160,
        gain: 0.42,
        type: 'triangle',
      });
      break;
    case 'stop':
      // Lower double tone — stopped / saved
      playTone({
        frequency: 330,
        durationMs: 130,
        gain: 0.45,
        type: 'triangle',
      });
      playTone({
        frequency: 220,
        durationMs: 180,
        gain: 0.45,
        type: 'triangle',
        delayMs: 140,
      });
      break;
    default:
      break;
  }
}

export function playRecordingStartBeep(): void {
  playBeep('start');
}

export function playRecordingPauseBeep(): void {
  playBeep('pause');
}

export function playRecordingStopBeep(): void {
  playBeep('stop');
}
